'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CreditType {
  id: string;
  name: string;
  description: string;
  price: string;
  verification: string;
  timeline: string;
  benefits: string[];
  icon: string;
}

interface ComparisonToolProps {
  creditTypes: CreditType[];
}

const ComparisonTool = ({ creditTypes }: ComparisonToolProps) => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>([creditTypes[0].id]);
  const [isHydrated, setIsHydrated] = useState(false);

  useState(() => {
    setIsHydrated(true);
  });

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 shadow-natural">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/2"></div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="h-64 bg-muted rounded"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const toggleSelection = (id: string) => {
    if (selectedTypes.includes(id)) {
      if (selectedTypes.length > 1) {
        setSelectedTypes(selectedTypes.filter(t => t !== id));
      }
    } else {
      if (selectedTypes.length < 3) {
        setSelectedTypes([...selectedTypes, id]);
      }
    }
  };

  const selectedCredits = creditTypes.filter(ct => selectedTypes.includes(ct.id));

  return (
    <div className="bg-card border border-border rounded-xl p-6 lg:p-8 shadow-natural">
      <div className="mb-6">
        <h3 className="font-heading font-bold text-2xl text-text-primary mb-2">
          Compare Carbon Credit Types
        </h3>
        <p className="text-text-secondary">
          Select up to 3 credit types to compare their features and benefits
        </p>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {creditTypes.map((type) => {
          const isSelected = selectedTypes.includes(type.id);
          return (
            <button
              key={type.id}
              onClick={() => toggleSelection(type.id)}
              className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                isSelected
                  ? 'border-primary bg-primary/10' :'border-border bg-surface hover:border-primary/50'
              }`}
            >
              <Icon name={type.icon as any} size={24} className={isSelected ? 'text-primary' : 'text-text-secondary'} />
              <p className={`text-sm font-medium mt-2 ${isSelected ? 'text-primary' : 'text-text-secondary'}`}>
                {type.name}
              </p>
            </button>
          );
        })}
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {selectedCredits.map((credit) => (
          <div key={credit.id} className="bg-surface border border-border rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={credit.icon as any} size={20} className="text-primary" />
              </div>
              <h4 className="font-heading font-bold text-lg text-text-primary">{credit.name}</h4>
            </div>
            
            <p className="text-sm text-text-secondary mb-4">{credit.description}</p>
            
            <div className="space-y-3 mb-4">
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-sm text-text-secondary">Price Range</span>
                <span className="text-sm font-medium text-text-primary">{credit.price}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-sm text-text-secondary">Verification</span>
                <span className="text-sm font-medium text-text-primary">{credit.verification}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-sm text-text-secondary">Timeline</span>
                <span className="text-sm font-medium text-text-primary">{credit.timeline}</span>
              </div>
            </div>
            
            <div>
              <p className="text-sm font-medium text-text-primary mb-2">Key Benefits</p>
              <ul className="space-y-2">
                {credit.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="CheckIcon" size={16} className="text-conversion flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-text-secondary">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ComparisonTool;